//@flow
import * as Immutable from 'immutable';

import EntityTypeEnum from 'common/enums/EntityTypeEnum';

import EntityImportDataHelper from 'common/entity/helpers/EntityImportDataHelper';

import {
    validatable,
    validate,
    validateAll,
    validateId,
    softValidateAll
} from 'common/utils/ValidationUtil';

import { MasterVehicleModel } from 'common/models/flow/MasterVehicleModel';
import type {
    MasterVehicleModelModelType,
    VehicleTypeEnum as VehicleType
} from 'common/models/flow/MasterVehicleModel';

import { SkillId } from './SkillId';
import { VehicleLoadingParams } from './VehicleLoadingParams';
import { AuditMetadata } from './AuditMetadata';

import DataUtils from 'common/utils/DataUtils';
import NumberUtils from 'common/utils/NumberUtils';
import RegExUtil from 'common/utils/RegExUtil';

import I18nHelper from 'common/helpers/I18nHelper';

import { TransporterId } from './TransporterId';
import { Volume } from './Volume';
import { Dimensions } from './Dimensions';

import { RiderPersonaLimits } from './RiderPersonaLimits';
import { VehicleTravelParams } from './VehicleTravelParams';
import { VolumeUnitEnum } from 'common/enums/VolumeUnitEnum';

import { MasterTransporter } from './MasterTransporter';

import { VehicleCompartment } from './VehicleCompartment';

import type { VolumeUnitType as MasterVolumeUnit } from 'common/models/flow/Volume';

const translationsNamespace = ['entityManagement', 'validations'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

const ENTITY_NAME_ID = 'masterVehicleModel';

export const VehicleTypeEnum = new EntityTypeEnum({
    NONE: 'NONE',
    TRUCK: 'TRUCK',
    BUS: 'BUS',
    BIKE: 'BIKE',
    CAR: 'CAR',
    PERSON: 'PERSON'
});
export const FuelTypeEnum = new EntityTypeEnum({
    PETROL: 'PETROL',
    DIESEL: 'DIESEL'
});

export const FleetTypeEnum = new EntityTypeEnum({
    CAPTIVE: 'CAPTIVE',
    THIRD_PARTY: 'THIRD_PARTY',
    ADHOC: 'ADHOC'
});

export const LoadingUnloadingSideEnum = new EntityTypeEnum({
    REAR: 'REAR',
    RIGHT: 'RIGHT',
    LEFT: 'LEFT',
    TOP: 'TOP'
});

const VolumeUnitType = {
    ItemCount: 'ITEM_COUNT',
    MetersCubic: 'METERS_CUBIC',
    LBHMeters: 'LBH_METERS'
};

export const validateUnloadingSide = type => {
    let validationMessage = null;
    const loadingUnloadingSideEnumValues = LoadingUnloadingSideEnum.values();

    if (DataUtils.isEmpty(type)) {
        validationMessage = nsTranslate(
            'validations:vehicleModelValidationErrors.loadingUnloadingSideEmpty'
        );
    } else if (
        !DataUtils.isEmpty(type) &&
        (!type.trim() || !loadingUnloadingSideEnumValues.includes(type))
    ) {
        validationMessage = nsTranslate(
            'validations:vehicleModelValidationErrors.loadingUnloadingSideEnumValidation',
            {
                typeList: loadingUnloadingSideEnumValues.join(', '),
                entityNameId: ENTITY_NAME_ID
            }
        );
    }
    return validationMessage;
};
export const validateVehicleType = (type: VehicleType) => {
    let validationMessage = null;
    const vehicleTypeEnumValues = VehicleTypeEnum.values();

    if (DataUtils.isEmpty(type)) {
        validationMessage = nsTranslate(
            'validations:vehicleModelValidationErrors.vehicleTypeEmpty'
        );
    } else if (
        !DataUtils.isEmpty(type) &&
        (!type.trim() || !vehicleTypeEnumValues.includes(type))
    ) {
        validationMessage = nsTranslate(
            'validations:vehicleModelValidationErrors.vehicleTypeEnumValidation',
            {
                typeList: vehicleTypeEnumValues.join(', '),
                entityNameId: ENTITY_NAME_ID
            }
        );
    }
    return validationMessage;
};

export const validateFuelType = fuelType => {
    let validationMessage = null;
    const fuelTypeValues = FuelTypeEnum.values();

    if (
        DataUtils.isEmpty(fuelType) ||
        (!DataUtils.isEmpty(fuelType) &&
            (!fuelType.trim() || !fuelTypeValues.includes(fuelType)))
    ) {
        validationMessage = nsTranslate(
            'entityValidationErrors.typeEnumValidation',
            {
                typeList: fuelTypeValues.join(', '),
                entityNameId: ENTITY_NAME_ID
            }
        );
    }
    return validationMessage;
};

export const validateFleetType = (fleetType: string) => {
    let validationMessage = null;
    const fleetTypeEnumValues = FleetTypeEnum.values();

    if (DataUtils.isEmpty(fleetType)) {
        validationMessage = nsTranslate(
            'validations:vehicleModelValidationErrors.fleetTypeEmpty'
        );
    } else if (!fleetTypeEnumValues.includes(fleetType)) {
        validationMessage = nsTranslate(
            'validations:vehicleModelValidationErrors.fleetTypeEnumValidation',
            {
                typeList: fleetTypeEnumValues.join(', '),
                entityNameId: ENTITY_NAME_ID
            }
        );
    }
    return validationMessage;
};

const validateNumber = (value: any) => {
    if (!DataUtils.isEmpty(value) && !NumberUtils.isNumeric(value)) {
        return nsTranslate(
            'validations:vehicleModelValidationErrors.notANumber'
        );
    }
};

const validateBoolean = (value: any) => {
    if (!DataUtils.isEmpty(value) && !DataUtils.isBoolean(value)) {
        return nsTranslate(
            'validations:vehicleModelValidationErrors.booleanValueValidation',
            {
                positiveLabel: nsTranslate(
                    'entityManagement:masterVehicleModelProperties.boolean.yes'
                ),
                negativeLabel: nsTranslate(
                    'entityManagement:masterVehicleModelProperties.boolean.no'
                )
            }
        );
    }
};

@validatable
class InternalMasterVehicleModel extends MasterVehicleModel {
    static fromJS(
        json: MasterVehicleModelModelType
    ): InternalMasterVehicleModel {
        const state: Object = Object.assign({}, json);
        state.transporterId = TransporterId.fromJS(state.transporterId);
        state.transporter = MasterTransporter.fromJS(state.transporter);
        state.skills = state.skills
            ? Immutable.List(state.skills).map(item => SkillId.fromJS(item))
            : state.skills;
        state.limits = state.limits
            ? RiderPersonaLimits.fromJS(state.limits)
            : state.limits;
        state.loadingParams = state.loadingParams
            ? VehicleLoadingParams.fromJS(state.loadingParams)
            : state.loadingParams;
        state.volume = Volume.fromJS(state.volume);
        state.vehicleVolume = state.vehicleVolume
            ? Volume.fromJS(state.vehicleVolume)
            : state.vehicleVolume;
        state.dimensions = state.dimensions
            ? Dimensions.fromJS(state.dimensions)
            : state.dimensions;
        state.containerDimensions = state.containerDimensions
            ? Dimensions.fromJS(state.containerDimensions)
            : state.containerDimensions;
        state.travelParams = state.travelParams
            ? VehicleTravelParams.fromJS(state.travelParams)
            : state.travelParams;
        state.auditMetadata = AuditMetadata.fromJS(state.auditMetadata);
        state.customProperties = Immutable.Map(state.customProperties);
        state.compartments = Immutable.List(state.compartments).map(item =>
            VehicleCompartment.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    get skills(): ?Immutable.List<SkillId> {
        return this._state.get('skills');
    }

    get resourceLimits(): RiderPersonaLimits {
        return this._state.get('resourceLimits');
    }

    get loadingParams(): ?VehicleLoadingParams {
        return this._state.get('loadingParams');
    }

    get auditMetadata(): AuditMetadata {
        return this._state.get('auditMetadata');
    }

    @validate
    setId(id: string): this {
        return super.setId(id);
    }

    validateId() {
        return validateId(this.id);
    }

    @validate
    setCode(code: string): this {
        return super
            .setCode(code)
            .setId(!DataUtils.isEmpty(code) ? RegExUtil.conformToId(code) : '');
    }

    validateCode() {
        const code = this.code || this.id;
        if (DataUtils.isEmpty(code)) {
            return nsTranslate('entityValidationErrors.codeEmpty');
        }
        if (code.trim().length < 2) {
            return nsTranslate('entityValidationErrors.codeLessThan2');
        }
        if (code.trim().length > 32) {
            return nsTranslate('entityValidationErrors.codeGreaterThan32');
        }
    }

    @validate
    setName(name: string): this {
        return super.setName(name);
    }

    validateName() {
        if (
            DataUtils.isEmpty(this.name) ||
            DataUtils.isEmpty(this.name.trim())
        ) {
            return nsTranslate('entityValidationErrors.nameEmpty', {
                entityNameId: ENTITY_NAME_ID
            });
        }
    }

    @validate
    setFuelType(fuelType: string): this {
        return super.setFuelType(fuelType);
    }

    validateFuelType() {
        return validateFuelType(this.fuelType);
    }

    @validate
    setVehicleType(vehicleType: string): this {
        return super.setVehicleType(vehicleType);
    }

    validateVehicleType() {
        return validateVehicleType(this.vehicleType);
    }

    @validate
    setFleetType(fleetType): this {
        return super.setFleetType(fleetType);
    }

    validateFleetType() {
        return validateFleetType(this.fleetType);
    }

    @validate
    setAllowMultiTrip(allowMultiTrip: boolean): this {
        return super.setAllowMultiTrip(allowMultiTrip);
    }

    validateAllowMultiTrip() {
        return validateBoolean(this.allowMultiTrip);
    }

    @validate
    setIsColdChain(isColdChain: string): this {
        return super.setIsColdChain(isColdChain);
    }

    validateIsColdChain() {
        return validateBoolean(this.isColdChain);
    }

    @validate
    setMileage(mileage: number): this {
        return super.setMileage(mileage);
    }

    validateMileage() {
        return validateNumber(this.mileage);
    }

    @validate
    setVolume(volume: string): this {
        return super.setVolume(volume);
    }

    validateVolume() {
        const { volume, dimensions, limits } = this;
        const { unit: volumeUnit } = volume;
        if (DataUtils.isEmpty(volume?.value)) {
            return nsTranslate(
                'validations:vehicleModelValidationErrors.volumeEmpty'
            );
        }
        if (
            dimensions?.isFilled() &&
            volumeUnit === VolumeUnitEnum.MetersCubic &&
            NumberUtils.isNumeric(volume.value) &&
            NumberUtils.isNumeric(dimensions?.getTotalVolume()) &&
            NumberUtils.toNumber(volume.value) >=
                NumberUtils.toNumber(dimensions?.getTotalVolume())
        ) {
            return nsTranslate(
                'validations:vehicleModelValidationErrors.containerVolumeGreaterThanDimension'
            );
        }
        if (
            !DataUtils.isEmpty(limits?.volume?.higherThresh) &&
            !DataUtils.isEmpty(volume?.value) &&
            NumberUtils.isNumeric(volume.value) &&
            NumberUtils.isNumeric(limits?.volume?.higherThresh) &&
            NumberUtils.toNumber(limits?.volume?.higherThresh) >
                NumberUtils.toNumber(volume.value)
        ) {
            return nsTranslate(
                'validations:vehicleModelValidationErrors.containerVolumeGreaterThanThreshold'
            );
        }
        return volume.validateValue();
    }

    @validate
    setVehicleVolume(vehicleVolume: any): this {
        return super.setVehicleVolume(vehicleVolume);
    }

    validateVehicleVolume() {
        const { volume, containerDimensions, vehicleVolume } = this;
        const { unit: volumeUnit } = volume;
        if (
            (containerDimensions?.isFilled() &&
                !DataUtils.isEmpty(vehicleVolume?.value) &&
                containerDimensions?.getTotalVolume() >= vehicleVolume.value) ||
            (!DataUtils.isEmpty(volume?.value) &&
                !DataUtils.isEmpty(vehicleVolume?.value) &&
                volume.value >= vehicleVolume.value &&
                volumeUnit === VolumeUnitEnum.MetersCubic)
        ) {
            return nsTranslate(
                'validations:vehicleModelValidationErrors.dimensionLessThanContainerVolume'
            );
        }
        return this.vehicleVolume?.validateValue();
    }

    @validate
    setMaxPayloadWeight(maxPayloadWeight: number): this {
        return super.setMaxPayloadWeight(maxPayloadWeight);
    }

    validateMaxPayloadWeight() {
        if (
            !DataUtils.isEmpty(this.limits?.weight?.higherThresh) &&
            !DataUtils.isEmpty(this.maxPayloadWeight) &&
            Number(this.limits.weight.higherThresh) >
                Number(this.maxPayloadWeight)
        ) {
            return nsTranslate(
                'validations:vehicleModelValidationErrors.maxPayloadWeightLessThanMaxWeight'
            );
        }
    }

    @validate
    setUserCount(userCount: number): this {
        return super.setUserCount(userCount);
    }

    validateUserCount() {
        return validateNumber(this.userCount);
    }

    @validate
    setUnladenWeight(unladenWeight: number): this {
        return super.setUnladenWeight(unladenWeight);
    }

    validateUnladenWeight() {
        return validateNumber(this.unladenWeight);
    }

    @validate
    setUnloadingSide(unloadingSide: string): this {
        return super.setUnloadingSide(unloadingSide);
    }

    validateUnloadingSide() {
        return validateUnloadingSide(this.unloadingSide);
    }

    @validate
    setTransporterId(transporterId: Object): this {
        return super.setTransporterId(transporterId);
    }

    validateTransporterId() {
        if (DataUtils.isEmpty(this.transporterId?.transporterId)) {
            return nsTranslate(
                'validations:vehicleModelValidationErrors.transporterIdsCannotEmpty'
            );
        }
    }

    @validate
    setTravelParams(travelParams: ?VehicleTravelParams): this {
        return super.setTravelParams(travelParams);
    }

    validateTravelParams(): ?string {
        return this.travelParams?.validateMaxSpeed();
    }

    @validateAll
    setAllValidationErrors(): this {
        let { limits, loadingParams, travelParams, compartments } = this;
        if (limits) {
            limits = limits.setAllValidationErrors();
        }
        if (loadingParams) {
            loadingParams = loadingParams.setAllValidationErrors();
        }
        if (travelParams) {
            travelParams = travelParams.setAllValidationErrors();
        }

        compartments = compartments.map(compartment =>
            compartment.setAllValidationErrors()
        );
        return this.setLimits(limits)
            .setLoadingParams(loadingParams)
            .setTravelParams(travelParams)
            .setCompartments(compartments);
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        const { limits, compartments } = this;
        if (limits?.hasValidationErrors()) {
            return true;
        }
        const hasCompartmentErrors = compartments.some(compartment =>
            compartment.hasValidationErrors()
        );
        return this.validateId() || this.validateName() || hasCompartmentErrors;
    }

    getCellValidationMessage({ header, validationData }) {
        return EntityImportDataHelper.getCellValidationMessage({
            entity: this,
            header,
            validationData
        });
    }

    sanitize(): this {
        const { dimensions, containerDimensions } = this;
        let sanitizedEntity = this;
        if (dimensions?.isEmpty()) {
            sanitizedEntity = sanitizedEntity.set(
                'dimensions',
                Immutable.fromJS(null)
            );
        }
        if (containerDimensions?.isEmpty()) {
            sanitizedEntity = sanitizedEntity.set(
                'containerDimensions',
                Immutable.fromJS(null)
            );
        }
        return sanitizedEntity;
    }

    // Alternative to lazy loading of nested models (init and cleaning)
    defaultSanitize() {
        let sanitizedVehicleModel = this.sanitize();
        sanitizedVehicleModel = super.defaultSanitize(sanitizedVehicleModel);
        if (sanitizedVehicleModel) {
            const sanitizedLoadingParams = sanitizedVehicleModel.loadingParams?.defaultSanitize();
            const sanitizedLimits = sanitizedVehicleModel.limits?.defaultSanitize();
            const sanitizedTravelParams = sanitizedVehicleModel.travelParams?.defaultSanitize();
            const sanitizedCompartments = sanitizedVehicleModel.compartments.map(
                compartment => compartment.defaultSanitize()
            );
            sanitizedVehicleModel = sanitizedVehicleModel.setLoadingParams(
                sanitizedLoadingParams
            );
            sanitizedVehicleModel = sanitizedVehicleModel.setVehicleVolume(
                null
            );
            sanitizedVehicleModel = sanitizedVehicleModel.setLimits(
                sanitizedLimits
            );
            sanitizedVehicleModel = sanitizedVehicleModel.setTravelParams(
                sanitizedTravelParams
            );
            sanitizedVehicleModel = sanitizedVehicleModel.setCompartments(
                sanitizedCompartments
            );
        }
        return sanitizedVehicleModel;
    }

    static defaultInit({ props }): InternalMasterVehicleModel {
        const loadingParams = VehicleLoadingParams.defaultInit({ props });
        const volume = {
            unit: VolumeUnitType.MetersCubic,
            value: null
        };
        const travelParams = VehicleTravelParams.defaultInit();
        const limits = RiderPersonaLimits.defaultInit();
        return InternalMasterVehicleModel.fromJS({
            allowMultiTrip: true,
            limitMultiTrip: false,
            maxAdditionalTrips: 1,
            isColdChain: false,
            isDeepFreeze: false,
            volume
        })
            .setLimits(limits)
            .setLoadingParams(loadingParams)
            .setTravelParams(travelParams);
    }

    setHiddenModelProperties(hiddenModelProperties: Immutable.List<string>) {
        return this.clone(
            this._state.set('hiddenModelProperties', hiddenModelProperties)
        );
    }

    getHiddenModelProperties(): Immutable.List<string> {
        return this._state.get('hiddenModelProperties');
    }

    isHidden(key: string) {
        const hiddenModelProperties = this.getHiddenModelProperties();
        const [groupKey] = key.split('.');
        if (key === 'limitMultiTrip' && !this.allowMultiTrip) {
            return true;
        }

        if (
            key === 'maxAdditionalTrips' &&
            (!this.allowMultiTrip || !this.limitMultiTrip)
        ) {
            return true;
        }

        return (
            (hiddenModelProperties && hiddenModelProperties.includes(key)) ||
            (hiddenModelProperties && hiddenModelProperties.includes(groupKey))
        );
    }

    get transporterId(): TransporterId {
        return this._state.get('transporterId');
    }

    get volume(): Volume {
        return this._state.get('volume');
    }

    get dimensions(): ?Dimensions {
        return this._state.get('dimensions');
    }

    get limits(): ?RiderPersonaLimits {
        return this._state.get('limits');
    }

    get vehicleVolume(): ?Volume {
        return this._state.get('vehicleVolume');
    }

    get containerDimensions(): ?Dimensions {
        return this._state.get('containerDimensions');
    }

    get travelParams(): ?VehicleTravelParams {
        return this._state.get('travelParams');
    }

    get compartments(): Immutable.List<VehicleCompartment> {
        return this._state.get('compartments');
    }

    static _validateMaxAdditionalTrips(entity: InternalMasterVehicleModel) {
        if (!entity.limitMultiTrip) {
            return;
        }
        const numberError = validateNumber(entity.maxAdditionalTrips);
        if (numberError) {
            return numberError;
        }

        if (DataUtils.isEmpty(entity.maxAdditionalTrips)) {
            return nsTranslate(
                'validations:vehicleModelValidationErrors.maxAdditionalTrips.empty'
            );
        }

        if (entity.maxAdditionalTrips < 1) {
            return nsTranslate(
                'validations:vehicleModelValidationErrors.maxAdditionalTrips.lowerLimit'
            );
        }
    }

    @validate
    setMaxAdditionalTrips(maxAdditionalTrips: number): this {
        return super.setMaxAdditionalTrips(maxAdditionalTrips);
    }

    validateMaxAdditionalTrips() {
        return InternalMasterVehicleModel._validateMaxAdditionalTrips(this);
    }

    static _validateLimitMultiTrip(entity: InternalMasterVehicleModel) {
        const booleanError = validateBoolean(entity.limitMultiTrip);

        if (booleanError) {
            return booleanError;
        }

        if (!entity.allowMultiTrip && entity.limitMultiTrip) {
            return nsTranslate(
                'validations:vehicleModelValidationErrors.limitMultiTrip.allowMultiTripDisabled'
            );
        }
    }

    @validate
    setLimitMultiTrip(limitMultiTrip: boolean): this {
        return super.setLimitMultiTrip(limitMultiTrip);
    }

    validateLimitMultiTrip() {
        return InternalMasterVehicleModel._validateLimitMultiTrip(this);
    }

    get transporter(): MasterTransporter {
        return this._state.get('transporter');
    }

    updateCompartmentsVolumeUnit(volumeUnit: ?MasterVolumeUnit): this {
        if (!volumeUnit) {
            return this;
        }
        const updatedCompartments = this.compartments.map(compartment => {
            const updatedVolume = compartment.volume.setUnit(volumeUnit);
            return compartment.setVolume(updatedVolume);
        });
        return this.setCompartments(updatedCompartments);
    }
}

export { InternalMasterVehicleModel as MasterVehicleModel };
